import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Edit3, MessageSquare, Sparkles, Save, Send, Loader, Clock, Zap, Users, Calendar, DollarSign, CalendarPlus, Phone, MapPin, Building, AlertCircle, CheckCircle, CheckCircle2, XCircle, ChevronDown, Lock, Trash2, ClipboardList, Briefcase } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { arrayUnion } from 'firebase/firestore';
import { LOSS_REASONS, LEAD_TYPES, DOCUMENT_STATUSES, PREQUALIFICATION_STATUSES, AWARD_STATUSES } from '../../lib/constants';
import { getActivityMeta, formatActivityMessage, createActivityEntry, ACTIVITY_TYPES, formatDate } from '../../utils/helpers';
import {
  generateSiteVisitCalendarUrl,
  generateCallCalendarUrl,
  generateEventCalendarUrl,
  openInGoogleCalendar
} from '../../utils/googleCalendar';
import { useGoogleCalendar } from '../../contexts/GoogleCalendarContext';
import { prequalificationService, contactService, citiesService, servicesService, tendersService, approvalService, sourcesService } from '../../services/api';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { SearchableCitySelect } from './ProfileModal';
import { useSocket } from '../../hooks/useSocket';
import { useLanguage } from '../../contexts/LanguageContext';
import { useUsers } from '../../hooks';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker, CustomCheckbox, Toast } from '../ui';
import DeleteConfirmModal from './DeleteConfirmModal';
import EnlistedConfirmModal from './EnlistedConfirmModal';
import ApproveRequestModal from './ApproveRequestModal';
import RejectRequestModal from './RejectRequestModal';

export default function PrequalificationDetailModal({
  prequalification,
  currentUser,
  onClose,
  onSave,
  onOpenAi,
  sources: propSources = [],
  managers = [],
  eventTypes = [],
  venues = [],
  onDelete,
  onRefreshLeads,
  onRefreshTenders
}) {
  const { t } = useLanguage();
  const { users = [] } = useUsers();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');
  const [toast, setToast] = useState(null);

  const [internalSources, setInternalSources] = useState(propSources || []);
  useEffect(() => {
    if (propSources && propSources.length > 0) {
      setInternalSources(propSources);
    } else {
      sourcesService.getAll().then(res => {
        if (Array.isArray(res)) setInternalSources(res);
      }).catch(err => console.error('Error fetching sources in PrequalificationDetailModal:', err));
    }
  }, [propSources]);
  const sources = internalSources;
  // Strip internal UI flags before initializing formData Ã¢â‚¬â€ prevents them from
  // leaking into onSave() calls and being persisted to the server/DB.
  const { _pendingWon: _stripPendingWon, _pendingSiteVisited: _stripPendingSiteVisited, ...cleanLeadData } = prequalification || {};

  const [formData, setFormData] = useState(() => {
    const data = { ...cleanLeadData };
    // siteVisitTime is not a separate DB column, so we extract it from the siteVisitDate
    if (data.siteVisitDate) {
      try {
        const d = new Date(data.siteVisitDate);
        if (!isNaN(d.getTime())) {
          // Format as HH:mm in local time
          data.siteVisitTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
        }
      } catch (e) { }
    }
    if (data.services && Array.isArray(data.services)) {
      data.services = data.services.map(s => typeof s === 'string' ? s : s.name).filter(Boolean);
    } else {
      data.services = [];
    }
    return data;
  });
  const [referenceData, setReferenceData] = useState(() => formData);

  const [comment, setComment] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEnlistedConfirm, setShowEnlistedConfirm] = useState(false);
  const [stageChangeConfirm, setStageChangeConfirm] = useState(null); // { from, to }
  const [stageChangeNote, setStageChangeNote] = useState('');
  // Loss reason Ã¢â‚¬â€ collected in the same confirm dialog when the target stage
  // is Lost, so it costs one extra click and can never be skipped.
  const [lossReason, setLossReason] = useState('');
  // What we told the customer. Required to enter Quoted Ã¢â‚¬â€ see handleStageClick.
  const [quotedAmount, setQuotedAmount] = useState('');

  // ── Approval Workflow State ───────────────────────────────────────────────
  const [approvalRequest, setApprovalRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const isApprover = ['CEO', 'GM_Operations', 'BM_Operations'].includes(currentUser?.role);
  const approvalStatus = formData.approvalStatus;

  // â”€â”€ Contact editing â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // A DM has no phone number, so the thread's sender id stands in as the
  // contact's identifier (`ig:997538Ã¢â‚¬Â¦`). The customer sends their real number
  // into the thread minutes later, and until it reaches the contact record the
  // CRM cannot recognise them: their first WhatsApp reply arrives from an
  // unknown number and becomes a second contact, a second prequalification and a second
  // owner. The number is in the room the whole time Ã¢â‚¬â€ there was just nowhere
  // on this screen to put it.
  const isPlaceholderPhone = (p) => /^(ig|fb|msg|meta-|unknown-)/i.test(String(p || ''));
  // Mirrors phoneProblem() on the server so the rep is told while they are
  // still looking at the number, not after a round trip. Deliberately narrow:
  // production holds landlines and years of imported oddities, and refusing
  // those would block edits to contacts that are otherwise fine. It catches
  // the one failure that matters â€” a mobile carrying the wrong digit count,
  // which is what "+92" typed in front of an 03â€¦ number produces. Seven
  // contacts are already in that state, silently matching nothing.
  const phoneProblem = (raw) => {
    if (!raw) return null;
    let d = String(raw).replace(/\D/g, '');
    if (!d) return t('phoneInvalid') === 'phoneInvalid' ? 'That does not look like a phone number.' : t('phoneInvalid');
    if (d.startsWith('0092') && d.length >= 14) d = d.slice(2);
    else if (d.startsWith('03') && d.length === 11) d = '92' + d.slice(1);
    else if (d.startsWith('3') && d.length === 10) d = '92' + d;
    else if (d.startsWith('920')) d = '92' + d.slice(3);
    const wrongLength = (n) => (t('phoneWrongLength') === 'phoneWrongLength'
      ? 'A mobile number should be 11 digits (03XX XXXXXXX). That one has {n}.'
      : t('phoneWrongLength')).replace('{n}', n);
    if (d.startsWith('923') && d.length !== 12) return wrongLength(d.length - 1);
    // A correct 03XXXXXXXXX became 923â€¦ above, so anything still starting 03 is
    // a mobile with the wrong digit count. Landlines (021, 041â€¦) are untouched.
    if (d.startsWith('03')) return wrongLength(d.length);
    if (d.length < 9) return t('phoneTooShort') === 'phoneTooShort' ? 'That number is too short.' : t('phoneTooShort');
    return null;
  };
  const [contactDraft, setContactDraft] = useState(null);
  // What the draft is measured against. Held locally rather than read from the
  // prequalification prop so "saved" doesn't depend on a list refetch landing: the write
  // succeeded, and the card should say so whether or not the refresh is slow,
  // cached or failed.
  const [contactBaseline, setContactBaseline] = useState(null);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactSaved, setContactSaved] = useState(false);
  const [contactConflict, setContactConflict] = useState(null);
  const [contactError, setContactError] = useState('');

  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);

  useEffect(() => {
    let isMounted = true;
    setLoadingCities(true);
    
    Promise.all([
      citiesService.getAll({ activeOnly: 'true' }).catch(err => {
        console.error('Error fetching cities for modal:', err);
        return [];
      }),
      servicesService.getAll().catch(err => {
        console.error('Error fetching services for modal:', err);
        return [];
      })
    ]).then(([citiesRes, servicesRes]) => {
      if (isMounted) {
        setCities(Array.isArray(citiesRes) ? citiesRes : []);
        setAvailableServices(Array.isArray(servicesRes) ? servicesRes : []);
        setLoadingCities(false);
      }
    });

    return () => { isMounted = false; };
  }, []);

  const handleCreateNewCity = async (cityName) => {
    const created = await citiesService.create({ name: cityName });
    if (created) {
      setCities((prev) => [...prev, created]);
      setContactDraft({ ...contactDraft, city: created.name });
      setContactSaved(false);
    } else {
      setContactDraft({ ...contactDraft, city: cityName });
      setContactSaved(false);
    }
  };

  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  useEffect(() => {
    const p = prequalification?.profile;
    if (!p) { setContactDraft(null); setContactBaseline(null); return; }
    const seed = {
      clientName: p.clientName || '',
      designation: p.designation || '',
      contactPersonName: p.contactPersonName || '',
      contactDetailExt: p.contactDetailExt || '',
      mobile: isPlaceholderPhone(p.mobile) ? '' : (p.mobile || ''),
      email: p.email || '',
      city: p.city || '',
      address: p.address || '',
      projectName: p.projectName || '',
      sourceId: p.sourceId || ''
    };
    setContactDraft(seed);
    setContactBaseline(seed);
    setContactConflict(null);
    setContactError('');
    setContactSaved(false);
    // Keyed on the contact, not its fields: re-seeding whenever a value changes
    // would wipe the rep's half-typed number the moment a refresh lands.
  }, [prequalification?.profile?.id]);

  const contactDirty = !!contactDraft && !!contactBaseline && (
    contactDraft.clientName !== contactBaseline.clientName
    || contactDraft.designation !== contactBaseline.designation
    || contactDraft.contactPersonName !== contactBaseline.contactPersonName
    || contactDraft.contactDetailExt !== contactBaseline.contactDetailExt
    || contactDraft.mobile !== contactBaseline.mobile
    || contactDraft.email !== contactBaseline.email
    || contactDraft.city !== contactBaseline.city
    || contactDraft.address !== contactBaseline.address
    || contactDraft.projectName !== contactBaseline.projectName
    || String(contactDraft.sourceId) !== String(contactBaseline.sourceId)
  );

  const handleSaveContact = async () => {
    if (!prequalification?.profile?.id || !contactDirty) return;
    setContactSaving(true);
    setContactConflict(null);
    setContactError('');
    // Only changed fields travel: the prequalifications list carries a partial contact
    // (no email), so sending the whole draft would blank what it never loaded.
    const payload = {};
    if (contactDraft.clientName !== contactBaseline.clientName) payload.clientName = contactDraft.clientName.trim();
    if (contactDraft.designation !== contactBaseline.designation) payload.designation = contactDraft.designation.trim();
    if (contactDraft.contactPersonName !== contactBaseline.contactPersonName) payload.contactPersonName = contactDraft.contactPersonName.trim();
    if (contactDraft.contactDetailExt !== contactBaseline.contactDetailExt) payload.contactDetailExt = contactDraft.contactDetailExt.trim();
    if (contactDraft.mobile && contactDraft.mobile !== contactBaseline.mobile) payload.mobile = contactDraft.mobile.trim();
    if (contactDraft.email !== contactBaseline.email) payload.email = contactDraft.email.trim();
    if (contactDraft.city !== contactBaseline.city) payload.city = contactDraft.city.trim();
    if (contactDraft.address !== contactBaseline.address) payload.address = contactDraft.address.trim();
    if (contactDraft.projectName !== contactBaseline.projectName) payload.projectName = contactDraft.projectName.trim();
    if (String(contactDraft.sourceId) !== String(contactBaseline.sourceId)) payload.sourceId = contactDraft.sourceId ? parseInt(contactDraft.sourceId) : null;
    try {
      await contactService.update(prequalification.profile.id, payload);
      setContactBaseline({ ...contactDraft });
      setContactSaved(true);
      // The prequalification list holds its own copy of the contact; refresh so the header,
      // the prequalifications table and the next open of this modal all agree.
      if (onRefreshLeads) await onRefreshLeads();
    } catch (err) {
      // 409 carries the contact that already owns the number Ã¢â‚¬â€ that is the
      // answer the rep needs, not an error to dismiss.
      const c = err?.payload?.conflict;
      if (c) setContactConflict(c);
      else setContactError(err?.message || 'Could not save the contact');
    } finally {
      setContactSaving(false);
    }
  };
  const [bookingConfirm, setBookingConfirm] = useState(null); // For booking confirmation modal
  // Tracks the source of a pending gated transition.
  // source: 'kanban' Ã¢â€ â€™ auto-close PrequalificationDetailModal on cancel
  // source: 'stagePill' Ã¢â€ â€™ only close booking modal on cancel
  // Object-based so future gated stages (e.g. Lost) can share the same pattern.
  const [pendingTransition, setPendingTransition] = useState(
    prequalification._pendingWon ? { source: 'kanban', from: prequalification.stage, to: 'Won' } : null
  );
  const [bookingData, setBookingData] = useState({
    venue: '',
    eventDate: '',
    eventType: '',
    guests: '',
    finalAmount: '',
    advanceAmount: '',
    shift: '',
    notes: '',
    clientCnic: '',
    clientAddress: '',
    clientEmail: ''
  });
  const [venueCheckStatus, setVenueCheckStatus] = useState(null); // null | 'checking' | 'available' | 'booked'
  const [venueBookedInfo, setVenueBookedInfo] = useState(null); // { clientName, orderNo, shift }
  const [isStageChanging, setIsStageChanging] = useState(false);
  const [calendarDeleteConfirm, setCalendarDeleteConfirm] = useState(null); // null | 'siteVisit' | 'call' | 'event'

  const todayStr = useMemo(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }, []);

  const isPastEventDate = false;

  // --- Quotation Items State ---
  const [quotationItems, setQuotationItems] = useState(() => {
    const s = prequalification.quotationItems || {};
    return {
      lawnRent: { lumpsum: s.lawnRent?.lumpsum || '', discount: s.lawnRent?.discount || '' },
      decoration: { lumpsum: s.decoration?.lumpsum || '', discount: s.decoration?.discount || '' },
      coldDrink: { enabled: s.coldDrink?.enabled || 'no', rate: s.coldDrink?.rate || '', discount: s.coldDrink?.discount || '', inputMode: s.coldDrink?.inputMode || 2, lumpsum: s.coldDrink?.lumpsum || '' },
      mineralWater: { enabled: s.mineralWater?.enabled || 'no', rate: s.mineralWater?.rate || '', discount: s.mineralWater?.discount || '', inputMode: s.mineralWater?.inputMode || 2, lumpsum: s.mineralWater?.lumpsum || '' },
      pantryCharges: { enabled: s.pantryCharges?.enabled || 'no', rate: s.pantryCharges?.rate || '', discount: s.pantryCharges?.discount || '' },
      freshFlower: { enabled: s.freshFlower?.enabled || 'no', rate: s.freshFlower?.rate || '', discount: s.freshFlower?.discount || '' },
      addons: s.addons || []
    };
  });

  const activityEndRef = useRef(null);
  const { syncLeadEvent, shouldAutoSync } = useGoogleCalendar();
  const [timelineActivities, setTimelineActivities] = useState([]);

  const [timelineLoading, setTimelineLoading] = useState(true);

  const fetchTimeline = async () => {
    setTimelineLoading(true);
    try {
      const activities = await prequalificationService.getTimeline(prequalification.id);
      setTimelineActivities(activities);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setTimelineLoading(false);
    }
  };


  useEffect(() => {
    if (prequalification.id) {
      fetchTimeline();
    }

    const handleTimelineUpdate = (e) => {
      if (e.detail?.prequalificationId === prequalification.id) {
        fetchTimeline();
      }
    };

    window.addEventListener('lead_timeline_updated', handleTimelineUpdate);
    return () => window.removeEventListener('lead_timeline_updated', handleTimelineUpdate);
  }, [prequalification.id]);

  // ── Fetch ApprovalRequest for this prequalification ──────────────────────
  useEffect(() => {
    if (!prequalification?.id) return;
    approvalService.getByEntity('PREQUALIFICATION', prequalification.id)
      .then(req => { if (req) setApprovalRequest(req); })
      .catch(err => console.error('[Approval] fetch error:', err));
  }, [prequalification?.id]);

  const handleApproveInModal = async ({ assignedSalespersonId, remarks }) => {
    try {
      let targetRequestId = approvalRequest?.id;
      if (!targetRequestId) {
        const fresh = await approvalService.getByEntity('PREQUALIFICATION', prequalification.id);
        if (fresh?.id) targetRequestId = fresh.id;
      }
      if (!targetRequestId) {
        throw new Error('Approval request ID not found. Please refresh and try again.');
      }
      const res = await approvalService.approve(targetRequestId, { assignedSalespersonId, remarks });
      setApprovalRequest(res.data);
      setFormData(prev => ({
        ...prev,
        approvalStatus: 'APPROVED',
        approvalRemarks: remarks || 'Approved by management'
      }));
      setShowApproveModal(false);
      setToast({ type: 'success', message: 'Prequalification approved successfully!' });
      if (onSave) onSave({ ...prequalification, approvalStatus: 'APPROVED', approvalRemarks: remarks });
      if (onRefreshLeads) onRefreshLeads();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Failed to approve' });
      throw err;
    }
  };

  const handleRejectInModal = async ({ remarks }) => {
    try {
      let targetRequestId = approvalRequest?.id;
      if (!targetRequestId) {
        const fresh = await approvalService.getByEntity('PREQUALIFICATION', prequalification.id);
        if (fresh?.id) targetRequestId = fresh.id;
      }
      if (!targetRequestId) {
        throw new Error('Approval request ID not found. Please refresh and try again.');
      }
      const res = await approvalService.reject(targetRequestId, { remarks });
      setApprovalRequest(res.data);
      setFormData(prev => ({
        ...prev,
        approvalStatus: 'REJECTED',
        approvalRemarks: remarks
      }));
      setShowRejectModal(false);
      setToast({ type: 'error', message: 'Prequalification rejected.' });
      if (onSave) onSave({ ...prequalification, approvalStatus: 'REJECTED', approvalRemarks: remarks });
      if (onRefreshLeads) onRefreshLeads();
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Failed to reject' });
      throw err;
    }
  };

  // â”€â”€ Sync form data and quotation items whenever prequalification prop is updated (e.g. from BMS webhook sync) â”€â”€
  const syncLeadToLocalState = (leadData) => {
    if (!leadData || !leadData.id) return;
    setFormData(prev => ({
      ...prev,
      stage: leadData.stage || leadData.status || prev.stage,
      status: leadData.status || leadData.stage || prev.status,
      clientName: leadData.clientName || prev.clientName,
      phone: leadData.phone || prev.phone,
      email: leadData.email || prev.email,
      clientBudget: leadData.clientBudget || '',
      quotationAmount: leadData.quotationAmount || leadData.finalAmount || prev.quotationAmount,
      guests: leadData.guests || prev.guests,
      eventDate: (function () {
        if (!leadData.eventDate) return '';
        try { return new Date(leadData.eventDate).toISOString().split('T')[0]; }
        catch (e) { return leadData.eventDate; }
      })(),
      shift: leadData.shift || prev.shift,
      eventType: leadData.eventType || prev.eventType,
      venue: leadData.venue || prev.venue,
      manager: leadData.manager || prev.manager,
      source: leadData.source || prev.source,
      notes: leadData.notes || prev.notes,
      siteVisited: leadData.siteVisited || prev.siteVisited
    }));

    if (leadData.quotationItems && typeof leadData.quotationItems === 'object') {
      const s = leadData.quotationItems;
      setQuotationItems({
        lawnRent: { lumpsum: s.lawnRent?.lumpsum || '', discount: s.lawnRent?.discount || '' },
        decoration: { lumpsum: s.decoration?.lumpsum || '', discount: s.decoration?.discount || '' },
        coldDrink: { enabled: s.coldDrink?.enabled || 'no', rate: s.coldDrink?.rate || '', discount: s.coldDrink?.discount || '', inputMode: s.coldDrink?.inputMode || 2, lumpsum: s.coldDrink?.lumpsum || '' },
        mineralWater: { enabled: s.mineralWater?.enabled || 'no', rate: s.mineralWater?.rate || '', discount: s.mineralWater?.discount || '', inputMode: s.mineralWater?.inputMode || 2, lumpsum: s.mineralWater?.lumpsum || '' },
        pantryCharges: { enabled: s.pantryCharges?.enabled || 'no', rate: s.pantryCharges?.rate || '', discount: s.pantryCharges?.discount || '' },
        freshFlower: { enabled: s.freshFlower?.enabled || 'no', rate: s.freshFlower?.rate || '', discount: s.freshFlower?.discount || '' },
        addons: s.addons || []
      });
    }
  };

  useEffect(() => {
    syncLeadToLocalState(prequalification);
  }, [prequalification]);

  // â”€â”€ Listen for real-time BMS webhook updates via socket â”€â”€
  const { socket } = useSocket();
  useEffect(() => {
    if (!socket || !prequalification?.id) return;

    const handleLeadUpdated = async (payload) => {
      // Only react if this event is for the currently open prequalification
      if (payload?.prequalificationId !== prequalification.id) return;

      console.log('Ã°Å¸â€â€ž [PrequalificationDetailModal] Real-time prequalification update received from', payload.source || 'unknown', 'Ã¢â‚¬â€ re-fetching prequalification data...');

      try {
        // Re-fetch the full prequalification from the API to get all updated fields including quotationItems
        const freshLead = await prequalificationService.getById(prequalification.id);
        if (freshLead) {
          // Build a prequalification object matching the shape the modal expects
          const updatedLead = {
            ...freshLead,
            clientName: freshLead.profile?.clientName || freshLead.contactPerson || freshLead.title || prequalification.clientName,
            stage: freshLead.status,
            email: freshLead.contact?.email || prequalification.email,
            manager: freshLead.assignee?.username || prequalification.manager,
            source: freshLead.source?.name || prequalification.source
          };
          syncLeadToLocalState(updatedLead);
          // Also refresh the timeline to show the new BMS_SYNC entry
          fetchTimeline();
        }
      } catch (err) {
        console.warn('[PrequalificationDetailModal] Failed to re-fetch prequalification after socket update:', err.message);
      }
    };

    socket.on('lead_updated', handleLeadUpdated);
    return () => socket.off('lead_updated', handleLeadUpdated);
  }, [socket, prequalification?.id]);

  // â”€â”€ Auto-trigger booking modal when opened via Kanban drag-to-Won â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // The Kanban board sets _pendingWon=true when a prequalification is dragged to Won column.
  // We detect this on mount and immediately open the Confirm Booking modal.
  useEffect(() => {
    const alreadyTerminal = ['Won', 'Completed'].includes(prequalification.stage) || ['Won', 'Completed'].includes(prequalification.status);
    if ((prequalification._pendingWon || prequalification._pendingSiteVisited) && !alreadyTerminal) {
      const timer = setTimeout(() => {
        setBookingData({
          venue: prequalification.venue || '',
          eventDate: (() => {
            if (!prequalification.eventDate) return '';
            try { return new Date(prequalification.eventDate).toISOString().split('T')[0]; }
            catch (e) { return prequalification.eventDate; }
          })(),
          eventType: prequalification.eventType || '',
          guests: prequalification.guests || '',
          finalAmount: prequalification.quotationAmount || '',
          advanceAmount: prequalification.advancePaid || '',
          shift: prequalification.shift || '',
          notes: prequalification.bookingNotes || '',
          clientCnic: prequalification.clientCnic || '',
          clientAddress: prequalification.clientAddress || '',
          clientEmail: prequalification.email || ''
        });
        setBookingConfirm({ from: prequalification.stage, to: prequalification._pendingWon ? 'Won' : 'Site Visited' });
        const preVenue = prequalification.venue || '';
        const preDate = prequalification.eventDate ? new Date(prequalification.eventDate).toISOString().split('T')[0] : '';
        const preShift = '';
        if (preVenue && preDate) {
          setTimeout(() => checkVenueAvailability(preVenue, preDate, preShift), 50);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // â”€â”€ Auto-trigger generic stage change confirm when opened via Kanban â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    const alreadyTerminal = ['Won', 'Completed'].includes(prequalification.stage) || ['Won', 'Completed'].includes(prequalification.status);
    if (prequalification._pendingStageChange && !alreadyTerminal) {
      const timer = setTimeout(() => {
        handleStageClick(prequalification._pendingStageChange);
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // â”€â”€ ESC key closes booking modal (P3) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Handles keyboard dismiss on desktop. On Kanban flow this also auto-closes
  // the PrequalificationDetailModal, consistent with Cancel/X/backdrop behaviour.
  useEffect(() => {
    if (!bookingConfirm) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleBookingCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingConfirm]);


  // Combine activity log and comments for timeline
  const timeline = useMemo(() => {
    const items = [];

    // Add API activities
    timelineActivities.forEach((a) => {
      let content = {};
      let raw = a.content;
      let isProfilePhase = false;
      if (typeof raw === 'string' && raw.startsWith('[Profile Phase] ')) {
        isProfilePhase = true;
        raw = raw.substring('[Profile Phase] '.length);
      }
      try {
        content = JSON.parse(raw);
      } catch (e) {
        content = { note: raw };
      }

      const isBmsSync = ['BMS_SYNC', 'bms_sync'].includes(a.type);
      items.push({
        type: a.type?.toLowerCase() || 'note_added',
        data: content,
        timestamp: new Date(a.createdAt),
        user: a.user?.username || (isBmsSync ? 'Banquet System (BMS)' : a.type === 'SYSTEM' ? 'System' : 'Unknown'),
        isProfileHistory: !!a.isProfileHistory || isProfilePhase
      });
    });

    return items.sort((a, b) => a.timestamp - b.timestamp);
  }, [timelineActivities]);

  // --- Quotation Calculations ---
  const quotationCalc = useMemo(() => {
    const g = Number(bookingConfirm ? bookingData.guests : formData.guests) || 0;
    const lump = (l, d) => Math.max(0, (Number(l) || 0) - (Number(d) || 0));
    const perPax = (r, q, d) => Math.max(0, (Number(r) || 0) * q - (Number(d) || 0));
    const lrSub = lump(quotationItems.lawnRent.lumpsum, quotationItems.lawnRent.discount);
    const decSub = lump(quotationItems.decoration.lumpsum, quotationItems.decoration.discount);
    // Cold Drink: lumpsum mode Ã¢â€ â€™ use lumpsum directly; perPax mode Ã¢â€ â€™ rate Ãƒâ€” guests
    const cdRawSub = quotationItems.coldDrink.enabled === 'yes'
      ? (quotationItems.coldDrink.inputMode === 1
        ? (Number(quotationItems.coldDrink.lumpsum) || 0)
        : (Number(quotationItems.coldDrink.rate) || 0) * g)
      : 0;
    const cdSub = Math.max(0, cdRawSub - (Number(quotationItems.coldDrink.discount) || 0));
    // Mineral Water: lumpsum mode Ã¢â€ â€™ use lumpsum directly; perPax mode Ã¢â€ â€™ rate Ãƒâ€” guests
    const mwRawSub = quotationItems.mineralWater.enabled === 'yes'
      ? (quotationItems.mineralWater.inputMode === 1
        ? (Number(quotationItems.mineralWater.lumpsum) || 0)
        : (Number(quotationItems.mineralWater.rate) || 0) * g)
      : 0;
    const mwSub = Math.max(0, mwRawSub - (Number(quotationItems.mineralWater.discount) || 0));
    // Derived per-person rates for display
    const cdPerPax = g > 0 && quotationItems.coldDrink.enabled === 'yes' && quotationItems.coldDrink.inputMode === 1
      ? (cdSub / g)
      : null;
    const mwPerPax = g > 0 && quotationItems.mineralWater.enabled === 'yes' && quotationItems.mineralWater.inputMode === 1
      ? (mwSub / g)
      : null;
    const pcSub = quotationItems.pantryCharges.enabled === 'yes' ? lump(quotationItems.pantryCharges.rate, quotationItems.pantryCharges.discount) : 0;
    const ffSub = quotationItems.freshFlower.enabled === 'yes' ? lump(quotationItems.freshFlower.rate, quotationItems.freshFlower.discount) : 0;
    const addonsSub = quotationItems.addons ? quotationItems.addons.reduce((sum, item) => sum + Math.max(0, (Number(item.lumpsum) || 0) - (Number(item.discount) || 0)), 0) : 0;
    const total = lrSub + decSub + cdSub + mwSub + pcSub + ffSub + addonsSub;
    return { lrSub, decSub, cdSub, mwSub, pcSub, ffSub, addonsSub, total, g, cdPerPax, mwPerPax };
  }, [quotationItems, formData.guests, bookingConfirm, bookingData.guests]);

  useEffect(() => {
    if (activeTab === 'activity')
      activityEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeTab, timeline]);

  // Helper to compute what changed
  const getChangedFieldsData = () => {
    const changes = [];
    const fieldsToCheck = [
      { key: 'clientName', label: 'Client Name' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'clientBudget', label: 'Client Budget' },
      { key: 'quotationAmount', label: 'Quotation Amount' },
      { key: 'guests', label: 'Guests' },
      { key: 'eventDate', label: 'Event Date' },
      { key: 'shift', label: 'Shift' },
      { key: 'eventType', label: 'Event Type' },
      { key: 'venue', label: 'Venue Preference' },
      { key: 'manager', label: 'Manager' },
      { key: 'source', label: 'Source' },
      { key: 'notes', label: 'Notes' },
      { key: 'siteVisitDate', label: 'Site Visit Date' },
      { key: 'siteVisitTime', label: 'Site Visit Time' },
      { key: 'siteVisited', label: 'Site Visited' },
      { key: 'leadType', label: 'Prequalification Form Type' },
      { key: 'contactPerson', label: 'Contact Person' },
      { key: 'documentStatus', label: 'Document Status' },
      { key: 'submissionDate', label: 'Submission Date' },
      { key: 'earnestMoneyRequired', label: 'Earnest Money Required' },
      { key: 'earnestMoneyValue', label: 'Earnest Money Value' },
      { key: 'prequalificationStatus', label: 'Prequalification Status' },
      { key: 'statusRemarks', label: 'Status Remarks' },
      { key: 'proposalValue', label: 'Proposal Value' },
      { key: 'grossProfit', label: 'Gross Profit' },
      { key: 'awardStatus', label: 'Award Status' }
    ];

    fieldsToCheck.forEach(field => {
      let originalValue = referenceData[field.key];
      let newValue = formData[field.key];

      // Normalize dates to YYYY-MM-DD for comparison
      if (field.key === 'eventDate' || field.key === 'siteVisitDate') {
        if (originalValue) originalValue = new Date(originalValue).toISOString().split('T')[0];
        if (newValue) newValue = new Date(newValue).toISOString().split('T')[0];
      }

      let strOriginal = originalValue === null || originalValue === undefined ? '' : String(originalValue).trim();
      let strNew = newValue === null || newValue === undefined ? '' : String(newValue).trim();

      if (strOriginal !== strNew) {
        changes.push({ label: field.label, old: strOriginal, new: strNew });
      }
    });

    return changes;
  };

  // â”€â”€ Calendar entry delete handler â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const calendarDeleteLabels = {
    siteVisit: { label: t('siteVisit') || 'Site Visit', date: formData.siteVisitDate },
    call: { label: t('scheduledCall') || 'Scheduled Call', date: formData.nextCallDate },
    event: { label: t('eventDate') || 'Event Date', date: formData.eventDate },
  };

  const handleCalendarEntryDelete = async () => {
    if (!calendarDeleteConfirm) return;

    let updatedData = { ...formData };
    switch (calendarDeleteConfirm) {
      case 'siteVisit':
        updatedData.siteVisitDate = null;
        updatedData.siteVisitTime = null;
        break;
      case 'call':
        updatedData.nextCallDate = null;
        updatedData.nextCallTime = null;
        break;
      case 'event':
        updatedData.eventDate = null;
        break;
      default:
        break;
    }

    setFormData(updatedData);
    setCalendarDeleteConfirm(null);

    // Auto-save so the call list updates immediately
    setIsSaving(true);
    try {
      await onSave(prequalification.id, {
        ...updatedData,
        quotationItems,
        quotationAmount: quotationCalc.total || updatedData.quotationAmount
      });
    } catch (error) {
      console.error("Failed to delete calendar entry:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    // Block saving if prequalification is locked
    if (isLockedStatus) {
      console.warn('Save blocked: prequalification is in a terminal stage.');
      return;
    }
    setIsSaving(true);

    // Track explicit changes
    const changes = getChangedFieldsData();

    if (changes.length > 0) {
      try {
        await prequalificationService.addNote(prequalification.id, {
          content: JSON.stringify({ 
            field: changes.map(c => c.label).join(', '),
            changes: changes
          }),
          type: ACTIVITY_TYPES.DETAILS_UPDATED,
          userId: currentUser?.id
        });
      } catch (error) {
        console.error('Failed to log detail update:', error);
      }
    }

    // Find source ID
    const sourceObj = sources?.find(s => s.name === formData.source);
    const sourceId = sourceObj ? sourceObj.id : null;

    let dataToSave = {
      ...formData,
      sourceId,
      quotationItems,
      quotationAmount: quotationCalc.total || formData.quotationAmount
    };

    // Merge siteVisitTime into siteVisitDate before saving
    if (dataToSave.siteVisitDate) {
      const dateStr = String(dataToSave.siteVisitDate).split('T')[0];
      const timeStr = dataToSave.siteVisitTime || '12:00';
      dataToSave.siteVisitDate = new Date(`${dateStr}T${timeStr}:00`).toISOString();
    }

    // Sanitize date fields: empty string '' causes Joi validation error on server
    const dateFieldsToClean = ['eventDate', 'siteVisitDate', 'bookedAt', 'expectedCloseDate'];
    dateFieldsToClean.forEach(field => {
      if (dataToSave[field] === '' || dataToSave[field] === undefined) {
        dataToSave[field] = null;
      }
    });

    // Save the prequalification with all formData + quotation items
    await onSave(prequalification.id, dataToSave);
    setReferenceData(formData);

    // Auto-sync calendar events if enabled
    try {
      // Check for site visit changes
      if (formData.siteVisitDate !== prequalification.siteVisitDate || formData.siteVisitTime !== prequalification.siteVisitTime) {
        if (shouldAutoSync('siteVisit')) {
          await syncLeadEvent({ ...formData, id: prequalification.id }, 'siteVisit', onSave);
        }
      }

      // Check for event date changes
      if (formData.eventDate !== prequalification.eventDate) {
        if (formData.stage === 'Won' && shouldAutoSync('booking')) {
          await syncLeadEvent({ ...formData, id: prequalification.id }, 'booking', onSave);
        } else if (shouldAutoSync('event')) {
          await syncLeadEvent({ ...formData, id: prequalification.id }, 'event', onSave);
        }
      }
    } catch (error) {
      console.error('Calendar sync error:', error);
    }

    setIsSaving(false);
    onClose();
  };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    try {
      // Create activity entry for the note
      const activityData = { note: comment };

      await prequalificationService.addNote(prequalification.id, {
        content: JSON.stringify(activityData),
        type: ACTIVITY_TYPES.NOTE_ADDED,
        userId: currentUser.id
      });

      setComment('');
      fetchTimeline(); // Refresh timeline
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleStageClick = (newStage) => {
    if (newStage === formData.stage) return; // Already on this stage

    // Block stage changes if prequalification is locked (Won/Lost/Completed)
    if (isLockedStatus) {
      console.warn('Stage change blocked: prequalification is in a terminal stage.');
      return;
    }

    // If moving to Won or Site Visited, show booking confirmation modal
    if (newStage === 'Won' || newStage === 'Site Visited') {
      setStageChangeConfirm({ from: formData.stage, to: newStage });
    } else if (newStage === 'Enlisted') {
      setShowEnlistedConfirm(true);
    } else {
      // Quoted has to mean a number was given to the customer. Until now it
      // meant a rep clicked a button: Won demands a final amount and Site
      // Visited a venue and date, but Quoted demanded nothing, so prequalifications sat at
      // that stage with PKR 0 Ã¢â‚¬â€ chased by quote follow-up reminders, counted
      // in the funnel, contributing nothing to pipeline. Seed the field from
      // the builder when it has been used, so the common case is one glance
      // and Confirm; a verbal quote over WhatsApp is typed in here instead.
      if (newStage === 'Quoted') {
        setQuotedAmount(String(quotationCalc.total || formData.quotationAmount || ''));
      }
      setStageChangeConfirm({ from: formData.stage, to: newStage });
    }
  };

  const handleStageChangeConfirm = async () => {
    if (!stageChangeConfirm) return;

    setIsStageChanging(true);
    const { from, to } = stageChangeConfirm;

    // Determine what else changed while confirming stage change
    const changes = getChangedFieldsData();

    // Create activity entry for stage change (includes any edited fields as part of STAGE_CHANGE activity)
    const isLoss = stageChangeConfirm.to === 'Lost' || stageChangeConfirm.to === 'Disqualified';
    const lossLabel = isLoss
      ? (LOSS_REASONS.find((r) => r.id === lossReason)?.label || lossReason)
      : null;

    const activityData = {
      from,
      to,
      note: stageChangeNote,
      // The reason rides the timeline entry as well as the prequalification field: a prequalification
      // can be revived and re-lost for a different reason, and both readings
      // are worth keeping.
      lossReason: lossLabel,
      editedFields: changes.length > 0 ? changes.map(c => c.label).join(', ') : null,
      changes: changes.length > 0 ? changes : undefined
    };

    try {
      await isActiveStageChange(to);

      // Every active stage sets a clock. The old list here named three stages
      // that no longer exist ('Follow Up', 'Warm', 'Hot'), so in practice only
      // Site Visited ever scheduled anything Ã¢â‚¬â€ a prequalification moved to Quoted got no
      // next-call date from anywhere, fell out of the Call List, and sat
      // invisible until someone stumbled on it (25 active prequalifications were in that
      // state when this was written, 22 of them Quoted or later). Cadence
      // matches the call-outcome defaults: Contacted tomorrow, Quoted and
      // Site Visited follow-up rhythm.
      const STAGE_CLOCK = {
        'Contacted': { days: 1, time: '10:00' },
        'Quoted': { days: 2, time: '11:00' },
        'Site Visited': { days: 1, time: '10:00' },
      };
      let nextCallUpdate = {};
      const clock = STAGE_CLOCK[to];
      // A rep's deliberately scheduled future call outranks the default Ã¢â‚¬â€ the
      // old code overwrote it. Auto-set only fills a blank or replaces a date
      // that has already passed (an expired clock is the same as no clock).
      const existingCall = String(formData.nextCallDate || prequalification.nextCallDate || '').split('T')[0];
      const todayStr = new Date().toLocaleDateString('en-CA');
      if (clock && (!existingCall || existingCall < todayStr)) {
        const nextDate = new Date();
        nextDate.setDate(nextDate.getDate() + clock.days);
        nextCallUpdate = {
          nextCallDate: nextDate.toLocaleDateString('en-CA'),
          nextCallTime: clock.time
        };
      }

      // Merge current formData edits with stage update so all changes are saved together
      const updateData = {
        ...formData,           // include any pending field edits (quotation amount, etc.)
        stage: to,
        stageUpdatedAt: new Date().toISOString(),
        stageUpdatedBy: currentUser.name,
        quotationItems,
        // On the way into Quoted the confirmed figure wins: it is either the
        // builder's total unchanged, or the number the rep typed over it.
        quotationAmount: to === 'Quoted'
          ? (Number(quotedAmount) || quotationCalc.total || formData.quotationAmount)
          : (quotationCalc.total || formData.quotationAmount),
        ...(isLoss ? { lostReason: lossReason, lostReasonNote: stageChangeNote || null } : {}),
        ...(to === 'Disqualified' ? { prequalificationStatus: 'Disqualified' } : {}),
        ...nextCallUpdate
      };

      // Sanitize date fields: empty string '' causes Joi validation error on server
      const dateFieldsToClean = ['eventDate', 'siteVisitDate', 'bookedAt', 'expectedCloseDate'];
      dateFieldsToClean.forEach(field => {
        if (updateData[field] === '' || updateData[field] === undefined) {
          updateData[field] = null;
        }
      });

      // Optimistic UI update
      setFormData(prev => ({
        ...prev,
        stage: to,
        ...(nextCallUpdate || {})
      }));
      setReferenceData(updateData);
      setStageChangeConfirm(null);
      setStageChangeNote('');
      setLossReason('');
      setQuotedAmount('');

      // Save stage change + all pending field edits in ONE request
      await onSave(prequalification.id, updateData);

      // Auto-sync calendar event if enabled and we scheduled a call
      if (nextCallUpdate.nextCallDate && shouldAutoSync('call')) {
        try {
          await syncLeadEvent({ ...prequalification, ...updateData }, 'call', onSave);
        } catch (calendarError) {
          console.error('Calendar sync error during stage change:', calendarError);
        }
      }

      // Log ONE combined activity entry for the stage change (which includes edited fields info)
      await prequalificationService.addNote(prequalification.id, {
        content: JSON.stringify(activityData),
        type: ACTIVITY_TYPES.STAGE_CHANGE,
        userId: currentUser.id
      });

      fetchTimeline();
    } catch (error) {
      console.error("Error changing stage:", error);
      // Revert optimistic update on failure
      setFormData(prev => ({
        ...prev,
        stage: from
      }));
    } finally {
      setIsStageChanging(false);
    }
  };

  const isActiveStageChange = async (to) => {
    // Placeholder if any validation needed
    return true;
  };

  const handleStageChangeCancel = () => {
    setStageChangeConfirm(null);
    setStageChangeNote('');
    setLossReason('');
    setQuotedAmount('');
  };

  const handleEnlistedConfirm = async (enlistedData) => {
    setIsStageChanging(true);
    try {
      // 1. Update the Prequalification status to 'Enlisted' + all editable fields from modal
      const updateData = {
        ...formData,
        stage: 'Enlisted',
        prequalificationStatus: 'Enlisted',
        stageUpdatedAt: new Date().toISOString(),
        stageUpdatedBy: currentUser.name,
        quotationItems,
        // Editable fields from EnlistedConfirmModal
        documentStatus:       enlistedData.documentStatus,
        submissionDate:       enlistedData.submissionDate || null,
        earnestMoneyRequired: enlistedData.earnestMoneyRequired,
        earnestMoneyValue:    enlistedData.earnestMoneyRequired ? (Number(enlistedData.earnestMoneyValue) || 0) : 0,
        proposalValue:        enlistedData.proposalValue ? Number(enlistedData.proposalValue) : formData.proposalValue,
        statusRemarks:        enlistedData.statusRemarks || formData.statusRemarks,
        ...(enlistedData.services?.length > 0 && { services: enlistedData.services }),
      };

      await onSave(prequalification.id, updateData);

      setFormData(prev => ({
        ...prev,
        ...updateData
      }));
      setReferenceData(updateData);

      // 2. Add Timeline note to Prequalification (rich activity log with all fields)
      const enlistedDetails = {};
      if (enlistedData.documentStatus)   enlistedDetails.documentStatus   = enlistedData.documentStatus;
      if (enlistedData.submissionDate)   enlistedDetails.submissionDate   = enlistedData.submissionDate;
      if (enlistedData.earnestMoneyRequired !== undefined) {
        enlistedDetails.earnestMoney = enlistedData.earnestMoneyRequired
          ? `Required — PKR ${Number(enlistedData.earnestMoneyValue || 0).toLocaleString()}`
          : 'Not Required';
      }
      if (enlistedData.services?.length > 0) enlistedDetails.services = enlistedData.services.join(', ');
      if (enlistedData.proposalValue)    enlistedDetails.proposalValue    = Number(enlistedData.proposalValue);
      if (enlistedData.statusRemarks)    enlistedDetails.statusRemarks    = enlistedData.statusRemarks;

      const activityData = {
        from: formData.stage,
        to: 'Enlisted',
        note: enlistedData.remarks
          ? `Transitioned to Enlisted. Remarks: ${enlistedData.remarks}`
          : 'Transitioned to Enlisted.',
        enlistedDetails,
      };

      await prequalificationService.addNote(prequalification.id, {
        content: JSON.stringify(activityData),
        type: ACTIVITY_TYPES.STAGE_CHANGE,
        userId: currentUser.id
      });
      fetchTimeline();
    } catch (error) {
      console.error("Error changing to Enlisted stage:", error);
    } finally {
      setIsStageChanging(false);
      setShowEnlistedConfirm(false);
    }
  };

  const handleBookingConfirm = async () => {
    if (!bookingConfirm) return;

    const isTentative = bookingConfirm.to === 'Site Visited';
    const finalAmt = quotationCalc.total || bookingData.finalAmount;

    // Validate required fields
    if (!bookingData.venue || !bookingData.eventDate) {
      return; // Venue and Event Date are required for both
    }

    // Strict validation for 'Won' stage
    if (!isTentative) {
      if (!finalAmt || !bookingData.clientCnic || !bookingData.clientAddress) {
        return;
      }
    }

    setIsStageChanging(true);
    const { from, to } = bookingConfirm;

    // Capture any pending formData field edits made before clicking Won
    const changes = getChangedFieldsData();

    try {
      // â”€â”€ BMS integration removed Ã¢â‚¬â€ AAA FM does not use external banquet system â”€â”€
      // Booking details are saved directly in CRM only.

      // â”€â”€ Save booking to CRM â”€â”€

      // Save to database Ã¢â‚¬â€ include pending formData edits + booking details in ONE request
      await onSave(prequalification.id, {
        ...formData,
        stage: to,
        venue: bookingData.venue,
        eventDate: bookingData.eventDate,
        eventType: bookingData.eventType,
        guests: parseInt(bookingData.guests) || null,
        finalAmount: Number(finalAmt),
        quotationAmount: Number(finalAmt),
        quotationItems,
        shift: bookingData.shift,
        clientCnic: bookingData.clientCnic,
        clientAddress: bookingData.clientAddress,
        bookingNotes: bookingData.notes,
        notes: bookingData.notes ? (formData.notes ? formData.notes + '\n\nBooking Note: ' + bookingData.notes : formData.notes) : formData.notes,
        bookedAt: new Date().toISOString(),
        bookedBy: currentUser.name,
        stageUpdatedAt: new Date().toISOString(),
        stageUpdatedBy: currentUser.name,
        ...(to === 'Won' && prequalification.stage !== 'Won' ? { order_date: new Date().toISOString().split('T')[0] } : {}),
      });

      // Log ONE combined activity Ã¢â‚¬â€ includes booking details and any pending field edits
      const activityData = {
        from,
        to,
        note: bookingData.notes || null,
        editedFields: changes.length > 0 ? changes.map(c => c.label).join(', ') : null,
        changes: changes.length > 0 ? changes : undefined,
        bookingDetails: {
          venue: bookingData.venue,
          eventDate: bookingData.eventDate,
          finalAmount: finalAmt,
          advanceAmount: bookingData.advanceAmount || 0,
          eventType: bookingData.eventType,
          guests: bookingData.guests,
          shift: bookingData.shift,
          clientCnic: bookingData.clientCnic,
          clientAddress: bookingData.clientAddress,
        }
      };

      await prequalificationService.addNote(prequalification.id, {
        content: JSON.stringify(activityData),
        type: ACTIVITY_TYPES.STAGE_CHANGE,
        userId: currentUser.id
      });
      fetchTimeline();

      // Optimistic UI update
      setFormData(prev => ({
        ...prev,
        stage: to,
        venue: bookingData.venue,
        eventDate: bookingData.eventDate,
        eventType: bookingData.eventType,
        guests: bookingData.guests,
        quotationAmount: Number(finalAmt),
        shift: bookingData.shift,
        clientCnic: bookingData.clientCnic,
        clientAddress: bookingData.clientAddress,
        bookingNotes: bookingData.notes,
        notes: bookingData.notes ? (prev.notes ? prev.notes + '\n\nBooking Note: ' + bookingData.notes : bookingData.notes) : prev.notes,
        bookedAt: new Date().toISOString(),
        bookedBy: currentUser.name,
        ...(to === 'Won' && prequalification.stage !== 'Won' ? { order_date: new Date().toISOString().split('T')[0] } : {}),
      }));
      setReferenceData(prev => ({
        ...prev,
        stage: to,
        venue: bookingData.venue,
        eventDate: bookingData.eventDate,
        eventType: bookingData.eventType,
        guests: bookingData.guests,
        quotationAmount: Number(finalAmt),
        shift: bookingData.shift,
        clientCnic: bookingData.clientCnic,
        clientAddress: bookingData.clientAddress,
        bookingNotes: bookingData.notes,
        notes: bookingData.notes ? (prev.notes ? prev.notes + '\n\nBooking Note: ' + bookingData.notes : bookingData.notes) : prev.notes,
        bookedAt: new Date().toISOString(),
        bookedBy: currentUser.name,
        ...(to === 'Won' && prequalification.stage !== 'Won' ? { order_date: new Date().toISOString().split('T')[0] } : {}),
      }));
      setBookingConfirm(null);
      setActiveTab('activity');

    } catch (error) {
      console.error('Error in booking flow:', error);
      // Revert stage on failure
      setFormData(prev => ({ ...prev, stage: from }));
    } finally {
      setIsStageChanging(false);
    }
  };

  const handleBookingCancel = () => {
    setBookingConfirm(null);
    setVenueCheckStatus(null);
    setVenueBookedInfo(null);
    // Kanban drag-to-Won flow: auto-close PrequalificationDetailModal so user returns
    // directly to the board with the card still in its original column.
    // Stage pill flow: keep PrequalificationDetailModal open (user stays in edit context).
    if (pendingTransition?.source === 'kanban') {
      setPendingTransition(null);
      onClose();
    }
  };

  // â”€â”€ Venue Availability Check â”€â”€
  const checkVenueAvailability = async (venueId, eventDate, shiftName) => {
    if (!venueId || !eventDate || !shiftName) {
      setVenueCheckStatus(null);
      setVenueBookedInfo(null);
      return;
    }
    setVenueCheckStatus('checking');
    setVenueBookedInfo(null);
    try {
      const data = { data: [] }; // Bookings API removed
      const bookingsList = data.data || [];
      // Find selected venue name and numeric banquet ID
      const venueObj = (venues && venues.length > 0 ? venues : []).find(v => v.id === venueId);
      const venueName = venueObj?.name || '';
      const venueNumericId = venueObj?.venue_id ?? venueObj?.numericId ?? venueObj?.venueId ?? null;

      // Helper: extract meaningful words (3+ chars, not pure numbers)
      const meaningfulWords = (str) =>
        str.toLowerCase().split(/[\s_\-]+/).filter(w => w.length >= 3 && isNaN(w));

      // Check if any booking on this date matches the venue AND shift
      const conflict = bookingsList.find(b => {
        const lawnName = (b.venue || '').toLowerCase().trim();
        const vName = venueName.toLowerCase().trim();
        if (!lawnName || !vName) return false;

        let isVenueMatch = false;
        // Tier 1: numeric venue_id match (most reliable)
        if (venueNumericId && b.lawn_id && Number(b.lawn_id) === Number(venueNumericId)) isVenueMatch = true;
        // Tier 2: exact or substring match
        else if (lawnName === vName || lawnName.includes(vName) || vName.includes(lawnName)) isVenueMatch = true;
        // Tier 3: word-level fuzzy match
        else {
          const words1 = meaningfulWords(lawnName);
          const words2 = meaningfulWords(vName);
          isVenueMatch = words1.some(w1 => words2.some(w2 => w1.includes(w2) || w2.includes(w1)));
        }

        if (!isVenueMatch) return false;

        // Shift match check
        const bShift = (b.shift || '').toLowerCase().trim();
        const targetShift = (shiftName || '').toLowerCase().trim();

        if (targetShift === 'day' || targetShift === 'morning') {
          if (bShift !== 'day' && bShift !== 'morning') return false;
        } else if (targetShift === 'night' || targetShift === 'evening') {
          if (bShift !== 'night' && bShift !== 'evening') return false;
        } else {
          if (bShift !== targetShift) return false;
        }

        return true;
      });
      if (conflict) {
        setVenueCheckStatus('booked');
        setVenueBookedInfo({
          clientName: conflict.clientName || 'Unknown',
          orderNo: '',
          shift: conflict.booking_timing_name || '',
          functionName: conflict.functionName || '',
        });
      } else {
        setVenueCheckStatus('available');
        setVenueBookedInfo(null);
      }
    } catch (err) {
      console.error('Venue availability check failed:', err);
      setVenueCheckStatus(null);
    }
  };
  const isBookingFormValid = bookingData.venue && bookingData.eventDate && Number(bookingData.guests) > 0 && (quotationCalc.total || bookingData.finalAmount) && venueCheckStatus !== 'booked' && (bookingConfirm?.to === 'Site Visited' ? true : (bookingData.clientCnic && bookingData.clientAddress));

  const sourceOptions = useMemo(() => {
    const list = [...(sources || [])];
    if (formData.source && !list.find((s) => s.name === formData.source)) {
      // Use a unique ID based on the source name to avoid collisions
      list.push({ id: `custom-${formData.source}`, name: formData.source });
    }
    return list;
  }, [sources, formData.source]);

  // --- Read-Only Check ---
  // If prequalification is Completed, we lock the stage/status changes.
  // DISABLED WON & LOST LOCKING (allow moving out of Lost if moved by mistake)
  const isLockedStatus = ['Completed'].includes(formData.status) || ['Completed'].includes(formData.stage);

  const isCeo = currentUser?.role === 'CEO';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="bg-white w-full sm:max-w-5xl h-[95vh] sm:h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden relative">

        {/* â”€â”€ Header â”€â”€ */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-start gap-3 sticky top-0 z-20">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                {prequalification?.profile?.projectName 
                  ? `${prequalification.profile.projectName} - ${formData.clientName}`
                  : formData.clientName}
              </h2>
              <span className={`px-2.5 py-0.5 text-[10px] sm:text-xs rounded-full border font-bold uppercase tracking-wider whitespace-nowrap ${formData.stage === 'Won' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                formData.stage === 'Lost' ? 'bg-red-50 text-red-700 border-red-200' :
                  'bg-slate-100 text-slate-700 border-slate-200'
                }`}>
                {formData.stage}
              </span>
              {approvalStatus === 'APPROVED' && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] sm:text-xs rounded-full border font-bold bg-emerald-50 text-emerald-700 border-emerald-200 whitespace-nowrap" title={formData.approvalRemarks || 'Approved'}>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  {t('approved') || 'Approved'}
                </span>
              )}
              {isLockedStatus && (
                <span className="flex items-center gap-1 px-2.5 py-0.5 bg-red-50 text-red-600 border border-red-200 rounded-full text-[10px] font-bold whitespace-nowrap">
                  <Lock className="w-3 h-3 shrink-0" /> {t('locked') || 'Locked'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
              {prequalification.createdAt && (
                <span className="flex items-center gap-1 font-medium whitespace-nowrap">
                  {/* Labelled, because a bare timestamp under a clock icon sits
                      next to an event date, a quote and a follow-up date, and
                      nothing said which of them it was. */}
                  <Clock className="w-3 h-3" />
                  <span className="text-gray-400 font-normal">{t('created') || 'Created'}</span>
                  {new Date(prequalification.createdAt).toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {/* A DM identifier under a phone icon reads as "we have their
                  number" Ã¢â‚¬â€ the exact belief that stops a rep asking for it. */}
              {formData.phone && !isPlaceholderPhone(formData.phone) && (
                <span className="flex items-center gap-1 font-mono font-bold text-gray-700">
                  <Phone className="w-3 h-3" />{formData.phone}
                </span>
              )}
              {isPlaceholderPhone(formData.phone) && (
                <span className="flex items-center gap-1 font-semibold text-amber-600 whitespace-nowrap">
                  <AlertCircle className="w-3 h-3" />{t('noPhoneYet') || 'No phone number yet'}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isCeo && onDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                title="Delete Prequalification"
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
            
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* â”€â”€ Stage pills â”€â”€ */}
        <div className="bg-slate-50 border-b border-gray-200 px-3 sm:px-6 py-2 flex gap-1.5 overflow-x-auto no-scrollbar">
          {(formData.leadType === 'PROPOSAL_TENDER' ? AWARD_STATUSES : PREQUALIFICATION_STATUSES).map((s) => (
            <button
              key={s}
              onClick={() => handleStageClick(s)}
              disabled={isLockedStatus}
              className={`px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-bold transition-all border whitespace-nowrap ${isLockedStatus
                ? 'opacity-40 cursor-not-allowed bg-white text-gray-400 border-gray-200'
                : formData.stage === s
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm scale-105'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-blue-400 hover:text-blue-600'
                }`}
            >
              {t(s) || s}
            </button>
          ))}
        </div>
        {/* ── Approval Workflow Banner ── */}
        {approvalStatus === 'PENDING_APPROVAL' && (
          <div className="mx-4 sm:mx-6 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs animate-fade-in">
            <div className="flex items-center gap-2.5 text-amber-900">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping shrink-0" />
              <div>
                <span className="font-bold">Pending Approval:</span> This prequalification is awaiting review by CEO or GM Operations.
              </div>
            </div>
            {isApprover && (
              <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                <button type="button" onClick={() => setShowApproveModal(true)}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold flex items-center gap-1 shadow-sm transition-all">
                  <CheckCircle2 className="w-3.5 h-3.5" /><span>Approve</span>
                </button>
                <button type="button" onClick={() => setShowRejectModal(true)}
                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold flex items-center gap-1 shadow-sm transition-all">
                  <XCircle className="w-3.5 h-3.5" /><span>Reject</span>
                </button>
              </div>
            )}
          </div>
        )}
        {approvalStatus === 'REJECTED' && (
          <div className="mx-4 sm:mx-6 mt-3 p-3 bg-rose-50 border border-rose-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs animate-fade-in">
            <div className="flex items-start gap-2.5 text-rose-900">
              <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold">Rejected / Revisions Requested</span>
                {formData.approvalRemarks && <p className="mt-0.5 text-rose-700 font-medium">"{formData.approvalRemarks}"</p>}
              </div>
            </div>
            {(formData.requestedById === currentUser?.id || prequalification.assignedTo === currentUser?.id || isApprover) && (
              <button type="button" onClick={async () => {
                try {
                  setIsResubmitting(true);
                  await approvalService.submit({ entityType: 'PREQUALIFICATION', entityId: prequalification.id, title: formData.title || `Prequalification #${prequalification.id}` });
                  const fresh = await approvalService.getByEntity('PREQUALIFICATION', prequalification.id);
                  if (fresh) setApprovalRequest(fresh);
                  setFormData(prev => ({ ...prev, approvalStatus: 'PENDING_APPROVAL', approvalRemarks: null }));
                  setToast({ type: 'success', message: 'Resubmitted for approval!' });
                  if (onSave) onSave({ ...prequalification, approvalStatus: 'PENDING_APPROVAL', approvalRemarks: null });
                  if (onRefreshLeads) onRefreshLeads();
                } catch (err) {
                  setToast({ type: 'error', message: err.message || 'Failed to resubmit' });
                } finally { setIsResubmitting(false); }
              }} disabled={isResubmitting}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center gap-1 shadow-sm transition-all shrink-0 self-end sm:self-auto disabled:opacity-50">
                {isResubmitting ? 'Resubmitting...' : 'Resubmit for Approval'}
              </button>
            )}
          </div>
        )}

        {/* â”€â”€ Stage Change Confirm â”€â”€ */}
        {stageChangeConfirm && (
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-30 rounded-t-2xl sm:rounded-2xl p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-6 w-full max-w-sm animate-scale-in">
              <h3 className="text-base font-bold text-gray-900 mb-1">{t('stageChangeConfirmTitle') || 'Confirm Stage Change'}</h3>
              <p className="text-sm text-gray-500 mb-4">
                {t('moveLeadFrom') || 'Move prequalification from '}
                <span className="font-semibold text-gray-800"> {stageChangeConfirm.from} </span>
                {t('moveLeadTo') || ' to '}
                <span className="font-semibold text-blue-600"> {stageChangeConfirm.to}</span>?
              </p>
              {/* Losing a prequalification asks why Ã¢â‚¬â€ one required click. Reasons are
                  grouped, but the salesperson only ever sees labels. */}
              {(stageChangeConfirm.to === 'Lost' || stageChangeConfirm.to === 'Disqualified') && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    {stageChangeConfirm.to === 'Lost'
                      ? (t('lossReasonLabel') || 'Why was this prequalification lost?')
                      : (t('disqualifiedReasonLabel') || 'Why was this prequalification disqualified?')} <span className="text-red-500">*</span>
                  </label>
                  <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
                    {LOSS_REASONS.filter(r => stageChangeConfirm.to === 'Lost' ? r.klass !== 'disqualified' : r.klass === 'disqualified').map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setLossReason(r.id)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${lossReason === r.id
                          ? 'bg-blue-50 text-blue-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        {t('loss_' + r.id) || r.label}
                      </button>
                    ))}
                  </div>
                  {lossReason === 'postponed' && (
                    <p className="text-[11px] text-amber-600 mt-1.5 leading-snug">
                      {t('lossPostponedHint') || 'Set a follow-up date on the prequalification before saving Ã¢â‚¬â€ postponed events are the warmest prequalifications you have.'}
                    </p>
                  )}
                </div>
              )}
              {/* Entering Quoted requires the figure the customer was given.
                  Prefilled from the builder when it has been used. */}
              {stageChangeConfirm.to === 'Quoted' && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    {t('amountQuotedLabel') || 'What amount did you quote?'} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">PKR</span>
                    <input
                      type="number"
                      autoFocus
                      value={quotedAmount}
                      onChange={(e) => setQuotedAmount(e.target.value)}
                      placeholder="1,250,000"
                      className="w-full pl-12 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  {quotationCalc.total > 0 && Number(quotedAmount) === quotationCalc.total ? (
                    <p className="text-[11px] text-gray-400 mt-1.5">
                      {t('amountFromBuilder') || 'From the quotation builder. Change it here if you quoted a different figure.'}
                    </p>
                  ) : (
                    <p className="text-[11px] text-gray-400 mt-1.5">
                      {t('amountQuotedHint') || 'A verbal or WhatsApp quote counts Ã¢â‚¬â€ type the figure you gave.'}
                    </p>
                  )}
                  {/* Not a blocker. A required field invites a throwaway number,
                      and a fake figure is worse than a blank one because it
                      silently inflates pipeline value. Every real quote on
                      record is above Rs 650,000, so flag anything far below. */}
                  {Number(quotedAmount) > 0 && Number(quotedAmount) < 100000 && (
                    <p className="text-[11px] text-amber-600 mt-1 leading-snug">
                      {t('amountQuotedLow') || 'That is far below any quote on record Ã¢â‚¬â€ check the figure before confirming.'}
                    </p>
                  )}
                </div>
              )}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  {(stageChangeConfirm.to === 'Lost' || stageChangeConfirm.to === 'Disqualified') && lossReason === 'other'
                    ? (t('notesRequired') || 'Notes (Required)')
                    : (t('notesOptional') || 'Notes (Optional)')}
                </label>
                <textarea
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm h-20 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={stageChangeNote}
                  onChange={(e) => setStageChangeNote(e.target.value)}
                  placeholder={(stageChangeConfirm.to === 'Lost' || stageChangeConfirm.to === 'Disqualified') && lossReason === 'competitor'
                    ? (t('lossCompetitorPlaceholder') || 'Which venue did they choose?')
                    : 'Add any notes about this stage change...'}
                />
              </div>
              <p className="text-xs text-gray-400 mb-4">{t('stageChangeTimelineLog') || 'This will be logged in the timeline.'}</p>
              <div className="flex gap-2 justify-end">
                <button onClick={handleStageChangeCancel} disabled={isStageChanging}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors disabled:opacity-50">
                  {t('cancel') || 'Cancel'}
                </button>
                <button
                  onClick={handleStageChangeConfirm}
                  disabled={isStageChanging
                    || ((stageChangeConfirm.to === 'Lost' || stageChangeConfirm.to === 'Disqualified') && (!lossReason || (lossReason === 'other' && !stageChangeNote.trim())))
                    || (stageChangeConfirm.to === 'Quoted' && !(Number(quotedAmount) > 0))}
                  className="px-5 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 min-w-[100px] justify-center">
                  {isStageChanging ? <Loader className="w-4 h-4 animate-spin" /> : (t('confirm') || 'Confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* â”€â”€ Delete Confirm Modal â”€â”€ */}
        {showDeleteConfirm && (
          <DeleteConfirmModal
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={() => {
              setShowDeleteConfirm(false);
              onDelete(prequalification.id);
            }}
          />
        )}

        {/* â”€â”€ Enlisted Confirm Modal â”€â”€ */}
        {showEnlistedConfirm && (
          <EnlistedConfirmModal
            isSaving={isStageChanging}
            onCancel={() => setShowEnlistedConfirm(false)}
            onConfirm={handleEnlistedConfirm}
            prequalification={{ ...prequalification, ...formData }}
          />
        )}



        <div className="flex-1 flex flex-col sm:flex-row overflow-hidden bg-slate-50">
          {/* â”€â”€ Sidebar tabs â”€â”€ */}
          <div className="w-full sm:w-48 bg-white border-b sm:border-b-0 sm:border-r border-gray-200 p-2 sm:p-3 flex flex-row sm:flex-col gap-1 overflow-x-auto sm:overflow-x-visible no-scrollbar shrink-0">
            <button
              onClick={() => setActiveTab('details')}
              className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${activeTab === 'details' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'
                }`}
            >
              <Edit3 className="w-4 h-4 shrink-0" />{t('details')}
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex-1 sm:flex-none text-left px-3 py-2.5 rounded-xl text-xs sm:text-sm font-semibold flex items-center justify-center sm:justify-start gap-2 whitespace-nowrap transition-colors ${activeTab === 'activity' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'text-gray-500 hover:bg-gray-50 border border-transparent'
                }`}
            >
              <Clock className="w-4 h-4 shrink-0" />{t('timeline')}
              {timeline.length > 0 && (
                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === 'activity' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {timeline.length}
                </span>
              )}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-8">
            {activeTab === 'details' && (
              <div className="max-w-2xl mx-auto space-y-6">
                {/* â”€â”€ Contact â”€â”€ the person, not the deal. Lives on its own
                {/* ——— Contact ——— the person, not the deal. Lives on its own
                    record, so it saves on its own button and reports its own
                    errors. */}
                {contactDraft && (
                  <div className="bg-indigo-50/30 p-4 rounded-xl border border-indigo-200 shadow-sm">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <h3 className="text-xs font-bold text-indigo-700 uppercase tracking-widest flex items-center gap-2">
                        <Users className="w-3.5 h-3.5" /> {t('profileHeading') !== 'profileHeading' ? t('profileHeading') : 'Profile'}
                      </h3>
                      {contactDirty && (
                        <button
                          onClick={handleSaveContact}
                          disabled={contactSaving || !!phoneProblem(contactDraft.mobile)}
                          className="px-3 py-1.5 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-60 flex items-center gap-1.5 transition-colors"
                        >
                          {contactSaving ? <Loader className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                          {t('saveProfile') || 'Save Profile'}
                        </button>
                      )}
                      {!contactDirty && contactSaved && (
                        <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" /> {t('saved') || 'Saved'}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* 1. Client Name */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('clientName') || 'Client Name'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterClientName') || 'Enter Client Name'}
                          value={contactDraft.clientName}
                          onChange={(e) => { setContactDraft({ ...contactDraft, clientName: e.target.value }); setContactSaved(false); }}
                        />
                      </div>
                      
                      {/* 2. Profile Person Name */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('contactPersonName') || 'Profile Person Name'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterContactName') || 'Enter Contact Person Name'}
                          value={contactDraft.contactPersonName}
                          onChange={(e) => { setContactDraft({ ...contactDraft, contactPersonName: e.target.value }); setContactSaved(false); }}
                        />
                      </div>

                      {/* 3. Designation */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('designation') || 'Designation'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterDesignation') || 'Enter Designation'}
                          value={contactDraft.designation}
                          onChange={(e) => { setContactDraft({ ...contactDraft, designation: e.target.value }); setContactSaved(false); }}
                        />
                      </div>

                      {/* 4. Profile Detail / Extension */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('contactDetailExt') || 'Profile Detail / Extension'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterExtension') || 'Enter Detail / Extension'}
                          value={contactDraft.contactDetailExt}
                          onChange={(e) => { setContactDraft({ ...contactDraft, contactDetailExt: e.target.value }); setContactSaved(false); }}
                        />
                      </div>

                      {/* 5. Email */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('email') || 'Email'}</label>
                        <input
                          type="email"
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterEmail') || 'Enter Email'}
                          value={contactDraft.email}
                          onChange={(e) => { setContactDraft({ ...contactDraft, email: e.target.value }); setContactSaved(false); }}
                        />
                      </div>

                      {/* 6. Mobile */}
                      <div>
                        <label className="text-xs font-bold text-gray-500 uppercase flex justify-between items-center">
                          <span>{t('mobile') || 'Mobile'}</span>
                          {!contactDraft.mobile && <span className="ml-2 text-[9px] font-medium text-amber-600 normal-case">{t('phoneMissingTag') || 'not set'}</span>}
                        </label>
                        <PhoneInput
                          country={'pk'}
                          value={contactDraft.mobile}
                          onChange={(phone) => { setContactDraft({ ...contactDraft, mobile: phone }); setContactSaved(false); setContactConflict(null); }}
                          containerClass="mt-1"
                          inputClass="!w-full !p-2 !text-sm !min-h-[38px] !pl-[48px] !border !border-gray-200 !rounded-lg !outline-none !transition-all focus:!ring-2 focus:!ring-blue-100 focus:!border-blue-500"
                          buttonClass="!border-gray-200 !bg-gray-50 !rounded-l-lg"
                          dropdownClass="!rounded-lg !shadow-2xl !border-gray-200 !max-h-[140px] !overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
                          inputProps={{ placeholder: t('enterMobile') || 'Enter Mobile' }}
                        />

                        {!!contactDraft.mobile && phoneProblem(contactDraft.mobile) && (
                          <p className="text-[11px] text-red-600 mt-1.5 leading-snug font-medium">
                            {phoneProblem(contactDraft.mobile)}
                          </p>
                        )}
                      </div>

                      {/* 7. Address */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('address') || 'Address'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterAddress') || 'Enter Address'}
                          value={contactDraft.address}
                          onChange={(e) => { setContactDraft({ ...contactDraft, address: e.target.value }); setContactSaved(false); }}
                        />
                      </div>

                      {/* 8. City */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('city') || 'City'}</label>
                        <SearchableCitySelect
                          value={contactDraft.city}
                          onChange={(val) => { setContactDraft({ ...contactDraft, city: val }); setContactSaved(false); }}
                          cities={cities}
                          onCreateCity={handleCreateNewCity}
                          loading={loadingCities}
                          onToggleOpen={setIsCityDropdownOpen}
                        />
                      </div>

                      {/* 9. Project Name */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('projectName') || 'Project Name'}</label>
                        <input
                          className="w-full p-2 text-sm min-h-[38px] border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                          placeholder={t('enterProjectName') || 'Enter Project Name'}
                          value={contactDraft.projectName}
                          onChange={(e) => { setContactDraft({ ...contactDraft, projectName: e.target.value }); setContactSaved(false); }}
                        />
                      </div>

                      {/* 10. Source */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase">{t('source') || 'Source'}</label>
                        <Dropdown>
                          <DropdownTrigger>
                            <button type="button" className="w-full p-2 border border-gray-200 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[38px] focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all outline-none">
                              {sources?.find(s => String(s.id) === String(contactDraft.sourceId))?.name || t('selectSource') || 'Select Source'}
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full z-[150]">
                            <DropdownMenu
                              selectedKeys={new Set([String(contactDraft.sourceId)])}
                              selectionMode="single"
                              onSelectionChange={(keys) => {
                                setContactDraft({ ...contactDraft, sourceId: Array.from(keys)[0] });
                                setContactSaved(false);
                              }}
                            >
                              {(sources || []).length ? (sources || []).map((s) => (
                                <DropdownItem key={s.id} id={String(s.id)}>
                                  {s.name}
                                </DropdownItem>
                              )) : <DropdownItem id="">{t('noSourcesAvailable') || 'No Sources'}</DropdownItem>}
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>
                    </div>

                    {/* A DM identifier is not a phone number and should not sit
                        in a field labelled Phone pretending to be one. */}
                    {isPlaceholderPhone(prequalification?.profile?.mobile) && (
                      <p className="text-[11px] text-gray-400 mt-3 font-mono">
                        {String(prequalification.profile.mobile).startsWith('fb:') ? 'Messenger' : 'Instagram'} &middot; {prequalification.profile.mobile}
                      </p>
                    )}

                    {contactConflict && (
                      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs text-amber-900 font-semibold">
                          {(t('phoneTakenBy') || 'That number already belongs to {name}').replace('{name}', contactConflict.name || 'another contact')}
                          {contactConflict.assignedTo ? ` \u2014 ${contactConflict.assignedTo}` : ''}
                          {contactConflict.leadStatus ? ` \u00B7 ${contactConflict.leadStatus}` : ''}
                        </p>
                        <p className="text-[11px] text-amber-700 mt-1 leading-snug">
                          {t('phoneTakenHint') === 'phoneTakenHint' ? 'They already exist in the CRM, most likely because they reached out previously before this contact was created. Open that prequalification and carry on there.' : t('phoneTakenHint')}
                        </p>
                        {contactConflict.prequalificationId && (
                          <button
                            onClick={() => { onClose(); setTimeout(() => navigate(`?prequalificationId=${contactConflict.prequalificationId}`), 150); }}
                            className="mt-2 text-xs font-bold text-blue-700 hover:underline flex items-center gap-1"
                          >
                            {t('openThatLead') === 'openThatLead' ? 'Open that prequalification' : t('openThatLead')} &rarr;
                          </button>
                        )}
                      </div>
                    )}
                    {contactError && <p className="text-xs text-red-600 mt-3">{contactError}</p>}
                  </div>
                )}

                {/* Meta Campaign Info - Show if prequalification has meta data */}
                {formData.meta && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4 text-blue-500" />
                      <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest">
                        {t('metaCampaignInfo')}
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      {formData.meta.campaign_name && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('campaign')}</span>
                          <div className="font-medium text-gray-900">{formData.meta.campaign_name}</div>
                        </div>
                      )}
                      {formData.meta.adset_name && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('adSet')}</span>
                          <div className="font-medium text-gray-900">{formData.meta.adset_name}</div>
                        </div>
                      )}
                      {formData.meta.ad_name && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('ad')}</span>
                          <div className="font-medium text-gray-900 truncate" title={formData.meta.ad_name}>
                            {formData.meta.ad_name}
                          </div>
                        </div>
                      )}
                      {formData.meta.platform && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('platform')}</span>
                          <div className="font-medium text-gray-900 capitalize">{formData.meta.platform}</div>
                        </div>
                      )}
                      {formData.meta.form_name && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('form')}</span>
                          <div className="font-medium text-gray-900">{formData.meta.form_name}</div>
                        </div>
                      )}
                      {formData.meta.city && (
                        <div>
                          <span className="text-gray-500 text-xs">{t('city')}</span>
                          <div className="font-medium text-gray-900">{formData.meta.city}</div>
                        </div>
                      )}
                    </div>

                    {/* Custom Fields Chips */}
                    {(formData.budgetRange || formData.meta.budget_range || formData.meta.event_type) && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-blue-200">
                        {(formData.budgetRange || formData.meta.budget_range) && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                            <DollarSign className="w-3 h-3" />
                            {formData.budgetRange || formData.meta.budget_range}
                          </span>
                        )}
                        {formData.meta.event_type && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {formData.meta.event_type}
                          </span>
                        )}
                        {formData.meta.guest_count && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            <Users className="w-3 h-3" />
                            {formData.meta.guest_count} {t('guests').toLowerCase()}
                          </span>
                        )}
                        {formData.meta.event_date && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            <Calendar className="w-3 h-3" />
                            {formData.meta.event_date}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-blue-50/30 p-6 rounded-xl border border-blue-200 shadow-sm space-y-4">
                  <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest border-b border-blue-200 pb-2 flex items-center gap-2">
                    {t('leadInformation') || 'Prequalification Information'}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('manager')}</label>
                      <Dropdown>
                        <DropdownTrigger>
                          <button
                            type="button"
                            className={`w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!['ceo', 'gm_operations', 'bm_operations', 'senior_team_lead', 'team_lead'].includes(currentUser?.role?.toLowerCase()) || isLockedStatus}
                          >
                            {formData.manager || t('selectManager')}
                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                          </button>
                        </DropdownTrigger>
                        <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                          <DropdownMenu
                            selectedKeys={new Set([formData.manager])}
                            selectionMode="single"
                            onSelectionChange={(keys) => setFormData({ ...formData, manager: Array.from(keys)[0] })}
                          >
                            {(managers || []).map((m, i) => (
                              <DropdownItem key={`${m}-${i}`} id={m}>
                                {m}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </DropdownPopover>
                      </Dropdown>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('source')}</label>
                      <Dropdown>
                        <DropdownTrigger>
                          <button
                            type="button"
                            className={`w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            {formData.source || t('unknown')}
                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                          </button>
                        </DropdownTrigger>
                        <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                          <DropdownMenu
                            selectedKeys={new Set([formData.source])}
                            selectionMode="single"
                            onSelectionChange={(keys) => setFormData({ ...formData, source: Array.from(keys)[0] })}
                          >
                            {sourceOptions.map((s, i) => (
                              <DropdownItem key={s.id || `source-${i}`} id={s.name}>
                                {s.name}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </DropdownPopover>
                      </Dropdown>
                    </div>

                    <div>
                      <label className="text-xs font-semibold text-gray-500">{t('services') || 'Services'}</label>
                      <Dropdown>
                        <DropdownTrigger>
                          <button
                            type="button"
                            className={`w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white text-left flex justify-between items-center ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            <div className="flex-1 truncate mr-2 text-left">
                              {formData.services?.length === 0 || !formData.services ? (
                                <span className="text-gray-500">{t('selectServices') || 'Select Services'}</span>
                              ) : formData.services?.length === 1 ? (
                                <span className="text-gray-800">{formData.services[0]}</span>
                              ) : (
                                <span className="text-blue-600 font-medium">
                                  {formData.services.length} {t('selected') || 'Selected'}
                                </span>
                              )}
                            </div>
                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                          </button>
                        </DropdownTrigger>
                        <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                          <DropdownMenu
                            selectedKeys={new Set(formData.services || [])}
                            selectionMode="multiple"
                            onSelectionChange={(keys) => setFormData({ ...formData, services: Array.from(keys) })}
                          >
                            {(availableServices || []).map((s) => (
                              <DropdownItem key={s.name} id={s.name}>
                                {s.name}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        </DropdownPopover>
                      </Dropdown>
                    </div>
                  </div>
                </div>

                {/* ——— Prequalification Details Card ——— */}
                <div className="bg-amber-50/30 p-6 rounded-xl border border-amber-200 shadow-sm space-y-4">
                  <div className="flex justify-between items-center border-b border-amber-200 pb-3">
                    <h3 className="text-xs font-bold text-amber-700 uppercase tracking-widest flex items-center gap-1.5">
                      <ClipboardList className="w-4 h-4 shrink-0" /> {t('prequalificationDetails') || 'Prequalification / Enlistment Details'}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">

                        <div>
                          <label className="text-xs font-semibold text-gray-600">Document Status</label>
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                type="button"
                                className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white text-left flex justify-between items-center min-h-[38px]"
                              >
                                {formData.documentStatus || 'To be Received'}
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full">
                              <DropdownMenu
                                selectedKeys={new Set([formData.documentStatus || 'To be Received'])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setFormData({ ...formData, documentStatus: Array.from(keys)[0] })}
                              >
                                {DOCUMENT_STATUSES.map((ds) => (
                                  <DropdownItem key={ds} id={ds}>
                                    {ds}
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-gray-600">Submission Date</label>
                          <DatePicker
                            className="mt-1"
                            value={formData.submissionDate ? new Date(formData.submissionDate).toISOString().split('T')[0] : ''}
                            onChange={(e) => setFormData({ ...formData, submissionDate: e.target.value })}
                          />
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-gray-600 block mb-1">Earnest Money</label>
                          <div className="flex items-center gap-3 border border-gray-200 rounded-lg p-2 bg-white min-h-[38px]">
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium whitespace-nowrap">
                              <input
                                type="radio"
                                name="emReqLeadDetail"
                                checked={!formData.earnestMoneyRequired}
                                onChange={() => setFormData({ ...formData, earnestMoneyRequired: false, earnestMoneyValue: 0 })}
                              />
                              Not Required
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-blue-700 whitespace-nowrap">
                              <input
                                type="radio"
                                name="emReqLeadDetail"
                                checked={formData.earnestMoneyRequired}
                                onChange={() => setFormData({ ...formData, earnestMoneyRequired: true })}
                              />
                              Required
                            </label>
                          </div>
                        </div>

                        {formData.earnestMoneyRequired && (
                          <div>
                            <label className="text-xs font-semibold text-gray-600">Earnest Money Value (PKR)</label>
                            <input
                              type="number"
                              className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white"
                              value={formData.earnestMoneyValue || ''}
                              onChange={(e) => setFormData({ ...formData, earnestMoneyValue: Number(e.target.value) })}
                              placeholder="PKR Amount"
                            />
                          </div>
                        )}

                        <div>
                          <label className="text-xs font-semibold text-gray-600">Status Box / Remarks</label>
                          <input
                            type="text"
                            className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px]"
                            value={formData.statusRemarks || ''}
                            onChange={(e) => setFormData({ ...formData, statusRemarks: e.target.value })}
                            placeholder="Remarks"
                          />
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-gray-600">Attachment / File Link</label>
                          <input
                            type="text"
                            className="w-full p-2 border border-gray-200 rounded-lg text-sm mt-1 bg-white min-h-[38px]"
                            value={formData.attachmentUrl || ''}
                            onChange={(e) => setFormData({ ...formData, attachmentUrl: e.target.value })}
                            placeholder="Attachment URL"
                          />
                        </div>
                    </div>
                  </div>







                {/* Calendar Actions */}
                {(formData.eventDate || formData.siteVisitDate || formData.nextCallDate) && (
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <CalendarPlus className="w-4 h-4 text-blue-600" />
                      <h3 className="text-xs font-bold text-blue-700 uppercase tracking-widest">
                        {t('addToGoogleCalendar')}
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.siteVisitDate && (
                        <div className="inline-flex items-center bg-white border border-blue-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => openInGoogleCalendar(generateSiteVisitCalendarUrl(formData))}
                            className={`inline-flex items-center gap-2 px-3 py-2 text-blue-700 text-sm font-medium hover:bg-blue-50 transition-colors ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            <MapPin className="w-4 h-4" />
                            {t('siteVisit')}
                            <span className="text-xs text-blue-500">
                              ({formatDate(formData.siteVisitDate)})
                            </span>
                          </button>
                          {!isLockedStatus && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setCalendarDeleteConfirm('siteVisit'); }}
                              className="px-2 py-2 text-blue-400 hover:text-red-500 hover:bg-red-50 border-l border-blue-200 transition-colors"
                              title="Remove Site Visit date"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                      {formData.nextCallDate && (
                        <div className="inline-flex items-center bg-white border border-green-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => openInGoogleCalendar(generateCallCalendarUrl(formData))}
                            className={`inline-flex items-center gap-2 px-3 py-2 text-green-700 text-sm font-medium hover:bg-green-50 transition-colors ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            <Phone className="w-4 h-4" />
                            {t('scheduledCall')}
                            <span className="text-xs text-green-500">
                              ({formatDate(formData.nextCallDate)})
                            </span>
                          </button>
                          {!isLockedStatus && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setCalendarDeleteConfirm('call'); }}
                              className="px-2 py-2 text-green-400 hover:text-red-500 hover:bg-red-50 border-l border-green-200 transition-colors"
                              title="Remove Scheduled Call date"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                      {formData.eventDate && (
                        <div className="inline-flex items-center bg-white border border-purple-200 rounded-lg overflow-hidden">
                          <button
                            onClick={() => openInGoogleCalendar(generateEventCalendarUrl(formData))}
                            className={`inline-flex items-center gap-2 px-3 py-2 text-purple-700 text-sm font-medium hover:bg-purple-50 transition-colors ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isLockedStatus}
                          >
                            <Calendar className="w-4 h-4" />
                            {formData.stage === 'Won' ? t('booked') : t('eventDate')}
                            <span className="text-xs text-purple-500">
                              ({formatDate(formData.eventDate)})
                            </span>
                          </button>
                          {!isLockedStatus && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setCalendarDeleteConfirm('event'); }}
                              className="px-2 py-2 text-purple-400 hover:text-red-500 hover:bg-red-50 border-l border-purple-200 transition-colors"
                              title="Remove Event Date"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-blue-500 mt-3">
                      {t('googleCalendarHelperText')}
                    </p>

                    {/* Calendar Delete Confirmation Modal */}
                    {calendarDeleteConfirm && (
                      <div className="mt-4 bg-white border border-red-200 rounded-xl p-4 shadow-sm animate-in fade-in duration-200">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-red-50 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-800 mb-1">
                              Remove {calendarDeleteLabels[calendarDeleteConfirm]?.label}?
                            </p>
                            <p className="text-xs text-gray-500 mb-3">
                              This will clear the date ({formatDate(calendarDeleteLabels[calendarDeleteConfirm]?.date)}) and remove it from the call list. You will need to click Save to apply the changes.
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setCalendarDeleteConfirm(null)}
                                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleCalendarEntryDelete}
                                className="px-3 py-1.5 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-teal-50/30 p-6 rounded-xl border border-teal-200 shadow-sm">
                  <h3 className="text-xs font-bold text-teal-700 uppercase tracking-widest border-b border-teal-200 pb-2 mb-4">
                    {t('notesSection')}
                  </h3>
                  <textarea
                    className={`w-full p-3 border rounded-lg text-sm h-32 resize-none focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={t('enterNotes') || 'Enter any additional notes here...'}
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    disabled={isLockedStatus}
                  />
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="flex flex-col h-full">
                {/* Timeline */}
                <div className="flex-1 space-y-4 mb-4 overflow-y-auto">
                  {timelineLoading ? (
                    [1, 2, 3].map(i => (
                      <div key={`sk-${i}`} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Skeleton variant="circular" width={20} height={20} animation="wave" />
                        </div>
                        <div className="flex-1 bg-white p-3 rounded-xl border shadow-sm">
                          <Skeleton width="60%" height={16} animation="wave" className="mb-2" />
                          <Skeleton width="40%" height={12} animation="wave" />
                        </div>
                      </div>
                    ))
                  ) : timeline.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>{t('noActivityYet')}</p>
                      <p className="text-xs mt-1">{t('addNoteToTrack')}</p>
                    </div>
                  ) : (
                    timeline.map((item, i) => {
                      // All items from API are activities with proper types
                      const meta = getActivityMeta(item.type);
                      return (
                        <div key={i} className="flex gap-3">
                          <div className={`w-8 h-8 rounded-full bg-${meta.color}-100 flex items-center justify-center flex-shrink-0`}>
                            <span className="text-sm">{meta.icon}</span>
                          </div>
                          <div className="flex-1 bg-white p-3 rounded-xl border shadow-sm">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                                {formatActivityMessage({ type: item.type, ...item.data })}
                              </p>
                              {item.isProfileHistory && (
                                <span className="inline-block bg-indigo-100 text-indigo-700 text-[10px] font-semibold px-2 py-0.5 rounded-full ml-2 shrink-0">
                                  {t('profilePhase') || 'Profile Phase'}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              {item.user} &bull; {item.timestamp.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={activityEndRef} />
                </div>

                {/* Add Note Input */}
                <div className="flex gap-2 pt-4 border-t">
                  <input
                    className={`flex-1 p-3 rounded-xl border outline-none text-sm ${isLockedStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder={t('addNotePlaceholder')}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                    disabled={isLockedStatus}
                  />
                  <button
                    onClick={handleAddComment}
                    className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-4 sm:px-6 py-3 bg-white border-t border-gray-200 flex justify-end gap-2 relative z-20 shrink-0">
          <button
            onClick={handleSave}
            disabled={isSaving || isLockedStatus || isPastEventDate}
            className={`px-5 py-2.5 text-sm text-white font-semibold rounded-xl flex items-center gap-2 transition-all ${isSaving || isLockedStatus || isPastEventDate ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-sm'
              }`}
          >
            {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isSaving ? (t('saving') || 'Saving...') : t('save')}
          </button>
        </div>
      </motion.div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

        {/* ── Approval Modals ── */}
        {showApproveModal && (
          <ApproveRequestModal
            request={approvalRequest || {
              id: prequalification.id,
              entityType: 'PREQUALIFICATION',
              title: formData.title || `Prequalification #${prequalification.id}`,
              requestedBy: prequalification.assignee || currentUser,
              createdAt: prequalification.createdAt
            }}
            users={users}
            onConfirm={handleApproveInModal}
            onClose={() => setShowApproveModal(false)}
          />
        )}
        {showRejectModal && (
          <RejectRequestModal
            request={approvalRequest || {
              id: prequalification.id,
              entityType: 'PREQUALIFICATION',
              title: formData.title || `Prequalification #${prequalification.id}`,
              requestedBy: prequalification.assignee || currentUser,
              createdAt: prequalification.createdAt
            }}
            onConfirm={handleRejectInModal}
            onClose={() => setShowRejectModal(false)}
          />
        )}
    </motion.div>
  );
}
